'use client';

import React, { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useLanguage } from '@/contexts/LanguageContext';

interface StatsData {
  totalTourists: number;
  totalRooms: number;
  totalNights: number;
}

const StatsSection = () => {
  const [stats, setStats] = useState<StatsData>({
    totalTourists: 400000,
    totalRooms: 600,
    totalNights: 900000,
  });
  const [loading, setLoading] = useState(true);
  const { t, formatNumber } = useLanguage();

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const { data, error } = await supabase
        .from('platform_stats')
        .select('*')
        .single();

      if (!error && data) {
        setStats({
          totalTourists: data.total_tourists || 400000,
          totalRooms: data.total_rooms || 600,
          totalNights: data.total_nights || 900000,
        });
      }
    } catch (err) {
      // Keep default values on error
    } finally {
      setLoading(false);
    }
  };

  const statsDisplay = [
    {
      value: stats.totalTourists,
      label: t('home.stats.tourists', { value: formatNumber(stats.totalTourists) }),
    },
    {
      value: stats.totalRooms,
      label: t('home.stats.rooms', { value: formatNumber(stats.totalRooms) }),
    },
    {
      value: stats.totalNights,
      label: t('home.stats.nights', { value: formatNumber(stats.totalNights) }),
    },
  ];

  return (
    <section data-component-id="src/app/homepage/components/StatsSection.tsx:63:4" data-component-path="src/app/homepage/components/StatsSection.tsx" data-component-line="63" data-component-end-line="87" data-component-file="StatsSection.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22className%22%3A%22py-16%20bg-background%22%7D" className="py-16 bg-background">
      <div data-component-id="src/app/homepage/components/StatsSection.tsx:64:6" data-component-path="src/app/homepage/components/StatsSection.tsx" data-component-line="64" data-component-end-line="86" data-component-file="StatsSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22container%20mx-auto%20px-4%22%7D" className="container mx-auto px-4">
        <div data-component-id="src/app/homepage/components/StatsSection.tsx:65:8" data-component-path="src/app/homepage/components/StatsSection.tsx" data-component-line="65" data-component-end-line="85" data-component-file="StatsSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-3%20gap-8%22%7D" className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {statsDisplay.map((stat, index) => (
            <div data-component-id="src/app/homepage/components/StatsSection.tsx:67:12" data-component-path="src/app/homepage/components/StatsSection.tsx" data-component-line="67" data-component-end-line="83" data-component-file="StatsSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20p-8%20bg-card%20rounded-xl%20shadow-lg%22%7D" key={index} className="text-center p-8 bg-card rounded-xl shadow-lg">
              {loading ? (
                <div data-component-id="src/app/homepage/components/StatsSection.tsx:69:16" data-component-path="src/app/homepage/components/StatsSection.tsx" data-component-line="69" data-component-end-line="72" data-component-file="StatsSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animate-pulse%22%7D" className="animate-pulse">
                  <div data-component-id="src/app/homepage/components/StatsSection.tsx:70:18" data-component-path="src/app/homepage/components/StatsSection.tsx" data-component-line="70" data-component-end-line="70" data-component-file="StatsSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-12%20bg-muted%20rounded%20mb-4%22%7D" className="h-12 bg-muted rounded mb-4"></div>
                  <div data-component-id="src/app/homepage/components/StatsSection.tsx:71:18" data-component-path="src/app/homepage/components/StatsSection.tsx" data-component-line="71" data-component-end-line="71" data-component-file="StatsSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-6%20bg-muted%20rounded%22%7D" className="h-6 bg-muted rounded"></div>
                </div>
              ) : (
                <>
                  <div data-component-id="src/app/homepage/components/StatsSection.tsx:75:18" data-component-path="src/app/homepage/components/StatsSection.tsx" data-component-line="75" data-component-end-line="77" data-component-file="StatsSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-5xl%20font-headline%20font-bold%20text-primary%20mb-4%22%2C%22textContent%22%3A%22%2B%22%7D" className="text-5xl font-headline font-bold text-primary mb-4">
                    {formatNumber(stat.value)}+
                  </div>
                  <div data-component-id="src/app/homepage/components/StatsSection.tsx:78:18" data-component-path="src/app/homepage/components/StatsSection.tsx" data-component-line="78" data-component-end-line="80" data-component-file="StatsSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-lg%20font-body%20text-muted-foreground%22%7D" className="text-lg font-body text-muted-foreground">
                    {stat.label.replace(/\d+[KM]?\+\s*/, '')}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default StatsSection;